/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router';
import { PlusIcon, Text, TextInput, TrashIcon, UnstyledButton } from '@/shared/ui';
import { SectionCard } from '@/features/property-management/components/PropertyDetailView/SectionCard';
import type { ClientRow } from "../../context/ClientManagementContext";
import { OpenAPIHousien } from "@services/requests/core/OpenAPI";
import { getValidAuthToken } from "@services/auth/auth";
import { generateAntiBotToken } from '@shared/utils/recaptcha';
import { useAgencyProperties } from '@features/visits/hooks/useAgencyProperties';

interface InterestRecord {
  interest_id: string;
  property_id: string;
  notes?: string;
}

/**
 * Builds the authenticated request headers shared by every interest call.
 *
 * @param withJson - Whether a JSON content type should be advertised.
 * @returns The header map including the bearer and anti-bot tokens.
 */
async function buildHeaders(withJson = false): Promise<Record<string, string>> {
  const token = await getValidAuthToken();
  const antiBotToken = await generateAntiBotToken('api_request').catch(() => null);
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (withJson) headers['Content-Type'] = 'application/json';
  if (antiBotToken) headers['X-Anti-Bot'] = antiBotToken;
  return headers;
}

/**
 * Client profile tab listing properties the client is interested in.
 *
 * Mirrors the structure of the visits and documents sections — a
 * {@link SectionCard} whose header carries the dashed call-to-action and
 * whose body lists rows with the same surface, border and typography as the
 * property detail view.
 *
 * @param props - The client row and agency identifier.
 * @returns The rendered properties-of-interest tab.
 */
export function ClientPropertiesInterestTab({ client, agencyId }: {
  client: ClientRow;
  agencyId: string;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation('features/clients');
  const [interests, setInterests] = useState<InterestRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Shared mapper: it understands the nested property model (location/description)
  // and yields a human label with an id-based fallback only as last resort.
  const { properties: allProperties, isLoading: isLoadingProperties } = useAgencyProperties(agencyId);

  const loadInterests = useCallback(async () => {
    try {
      const response = await fetch(
        `${OpenAPIHousien.BASE}/agency/${agencyId}/clients/${client.client_id}/interests`,
        { headers: await buildHeaders() },
      );
      if (response.ok) {
        const data = await response.json();
        setInterests(data);
      }
    } catch { setInterests([]); }
  }, [agencyId, client.client_id]);

  useEffect(() => {
    let cancelled = false;
    loadInterests().finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [loadInterests]);

  const interestPropertyIds = interests.map((i) => i.property_id);
  const query = searchQuery.trim().toLowerCase();
  const availableProperties = allProperties.filter((p) =>
    !interestPropertyIds.includes(p.id)
    // Match the label and sublabel the user actually sees, not a field that no
    // longer exists on the nested model.
    && (!query
      || p.label.toLowerCase().includes(query)
      || p.sublabel.toLowerCase().includes(query))
  );

  const addProperty = async (propertyId: string) => {
    try {
      await fetch(
        `${OpenAPIHousien.BASE}/agency/${agencyId}/clients/${client.client_id}/interests`,
        {
          method: 'POST',
          headers: await buildHeaders(true),
          body: JSON.stringify({ property_id: propertyId }),
        }
      );
      await loadInterests();
    } catch { /* handled by global */ }
    setShowSearch(false);
    setSearchQuery('');
  };

  const removeInterest = async (interestId: string) => {
    try {
      await fetch(
        `${OpenAPIHousien.BASE}/agency/${agencyId}/clients/${client.client_id}/interests/${interestId}`,
        { method: 'DELETE', headers: await buildHeaders() },
      );
      await loadInterests();
    } catch { /* handled by global */ }
  };

  const title = t('profile.tabs.propertiesOfInterest');

  if (isLoading || isLoadingProperties) {
    return (
      <SectionCard data-testid="client-properties-interest" title={title}>
        <div className="flex flex-col gap-[var(--space-3)]">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-[52px] rounded-(--radius-md) bg-[var(--surface-tertiary)] animate-pulse" />
          ))}
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      data-testid="client-properties-interest"
      title={title}
      titleAccessory={(
        <AddPropertyButton
          label={t('profile.properties.addProperty')}
          onClick={() => setShowSearch(!showSearch)}
        />
      )}
    >
      {showSearch && (
        <PropertySearchPanel
          searchQuery={searchQuery}
          onQueryChange={setSearchQuery}
          properties={availableProperties}
          onSelect={addProperty}
          placeholder={t('profile.properties.searchPlaceholder')}
          noResultsLabel={t('profile.properties.noResults')}
        />
      )}

      {interests.length === 0 ? (
        <div className="flex items-center justify-center h-[120px] text-[var(--text-tertiary)] text-[length:var(--text-body-sm)]">
          {t('profile.properties.empty')}
        </div>
      ) : (
        <div className="flex flex-col gap-[var(--space-3)]">
          {interests.map((interest) => {
            const property = allProperties.find((p) => p.id === interest.property_id);
            return (
              <InterestRow
                key={interest.interest_id}
                interest={interest}
                title={property?.label || interest.property_id}
                subtitle={property?.sublabel}
                removeLabel={t('profile.properties.removeProperty')}
                onOpen={() => navigate(`/app/agencies/${agencyId}/properties/${interest.property_id}`)}
                onRemove={() => removeInterest(interest.interest_id)}
              />
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

/**
 * Dashed call-to-action that reveals the property search panel. Mirrors the
 * schedule-visit and upload-document CTAs so every section header reads the same.
 *
 * @param props - The button label and click handler.
 * @returns The rendered add-property button.
 */
function AddPropertyButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <UnstyledButton
      type="button"
      data-testid="client-add-property-interest"
      onClick={onClick}
      aria-label={label}
      className="flex items-center gap-(--space-1-5) rounded-(--radius-md) border border-dashed border-[var(--border-subtle)] px-(--space-3) py-(--space-1-5) text-[var(--seed-base)] cursor-pointer bg-transparent hover:bg-[var(--surface-hover)]"
    >
      <PlusIcon width={13} height={13} />
      <Text variant="meta" as="span" className="[font-weight:var(--font-semibold)]">{label}</Text>
    </UnstyledButton>
  );
}

interface SearchPanelProps {
  searchQuery: string;
  onQueryChange: (value: string) => void;
  properties: Array<{ id: string; label: string; sublabel: string }>;
  onSelect: (propertyId: string) => void;
  placeholder: string;
  noResultsLabel: string;
}

/**
 * Inline search panel used to attach a new property to the client.
 *
 * @param props - The query state, the selectable properties and display copy.
 * @returns The rendered search panel.
 */
function PropertySearchPanel({
  searchQuery,
  onQueryChange,
  properties,
  onSelect,
  placeholder,
  noResultsLabel,
}: SearchPanelProps) {
  return (
    <div className="mb-[var(--space-3)] rounded-(--radius-md) border border-[var(--border-subtle)] bg-[var(--surface-primary)] p-[var(--space-3)]">
      <TextInput
        value={searchQuery}
        onChange={(e) => onQueryChange(e.target.value)}
        placeholder={placeholder}
        autoFocus
        className="mb-[var(--space-2)] w-full"
      />
      <div className="flex max-h-[160px] flex-col gap-[var(--space-1)] overflow-y-auto">
        {properties.slice(0, 10).map((p) => (
          <UnstyledButton
            key={p.id}
            type="button"
            onClick={() => onSelect(p.id)}
            className="block w-full rounded-(--radius-sm) px-[var(--space-2)] py-[var(--space-2)] text-left hover:bg-[var(--surface-hover)]"
          >
            <Text variant="bodySm" as="span" className="block [font-weight:var(--font-medium)] text-[var(--text-primary)]">
              {p.label}
            </Text>
            {p.sublabel && p.sublabel !== p.label && (
              <Text variant="micro" as="span" className="block text-[var(--text-tertiary)]">
                {p.sublabel}
              </Text>
            )}
          </UnstyledButton>
        ))}
        {properties.length === 0 && (
          <Text variant="bodySm" as="span" className="px-[var(--space-2)] py-[var(--space-2)] text-[var(--text-tertiary)]">
            {noResultsLabel}
          </Text>
        )}
      </div>
    </div>
  );
}

interface InterestRowProps {
  interest: InterestRecord;
  title: string;
  subtitle?: string;
  removeLabel: string;
  onOpen: () => void;
  onRemove: () => void;
}

/**
 * Renders a single property-of-interest row: headline, address and notes with
 * a trailing remove action. Shares the visit-row surface and typography.
 *
 * @param props - The interest record, display copy and row handlers.
 * @returns The rendered interest row.
 */
function InterestRow({ interest, title, subtitle, removeLabel, onOpen, onRemove }: InterestRowProps) {
  return (
    <div className="flex justify-between items-center px-[var(--space-3)] py-[var(--space-2)] rounded-(--radius-md) bg-[var(--surface-primary)] border border-[var(--border-ultra-subtle)]">
      <UnstyledButton
        type="button"
        onClick={onOpen}
        className="min-w-0 flex-1 text-left cursor-pointer bg-transparent"
      >
        <Text variant="bodySm" as="span" className="block truncate [font-weight:var(--font-medium)] text-[var(--text-primary)]">
          {title}
        </Text>
        <Text variant="micro" as="span" className="block truncate text-[var(--text-tertiary)]">
          {subtitle || '—'}
        </Text>
        {interest.notes && (
          <Text variant="micro" as="span" className="block truncate text-[var(--text-tertiary)]">
            {interest.notes}
          </Text>
        )}
      </UnstyledButton>
      <UnstyledButton
        type="button"
        onClick={onRemove}
        aria-label={removeLabel}
        className="shrink-0 p-[var(--space-1)] text-[var(--text-tertiary)] cursor-pointer bg-transparent hover:text-[var(--status-error)]"
      >
        <TrashIcon width={14} height={14} />
      </UnstyledButton>
    </div>
  );
}
